import type { WorkflowConfig, WorkflowStep } from '@/context/MasterDataContext'
import type { ApprovalStep } from '@/types'

/**
 * Resolves the configured approval steps for a specific business module from Workflow Master.
 * Returns an empty array if the workflow for the module is disabled or not configured.
 */
export function resolveWorkflowChain(
  workflows: WorkflowConfig[],
  moduleName: string
): ApprovalStep[] {
  if (!workflows || !Array.isArray(workflows)) return []
  
  const source = workflows.find(
    w => w && w.module && w.module.toLowerCase() === moduleName.toLowerCase() && w.isActive && w.status !== 'inactive'
  )
  
  if (!source?.steps?.length) return []

  return source.steps.map((s, i) => ({
    id: `AS${String(i + 1).padStart(2, '0')}`,
    stepNo: s.stepNo ?? i + 1,
    stepName: s.stepName || `Step ${i + 1}`,
    approverName: s.approverName || 'Approver',
    role: s.stepName || 'Approver',
    isRequired: s.isRequired !== false,
    action: s.action || 'both',
    status: 'pending' as const,
  }))
}

/**
 * Returns whether a module has an active, enabled workflow configured.
 */
export function hasActiveWorkflow(
  workflows: WorkflowConfig[],
  moduleName: string
): boolean {
  if (!workflows || !Array.isArray(workflows)) return false
  const source = workflows.find(
    w => w && w.module && w.module.toLowerCase() === moduleName.toLowerCase() && w.isActive && w.status !== 'inactive'
  )
  return !!(source && source.steps && source.steps.length > 0)
}
